import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../hooks/usePageTitle';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { verifyMarketplacePayment } from '../services/api';
import '../styles/marketplace.css';

export default function MarketplacePaymentSuccess() {
    const { t } = useTranslation();
    usePageTitle(t('marketplace.paymentTitle'));
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('verifying'); // verifying | success | pending | error

    useEffect(() => {
        const paymentId = searchParams.get('payment_id');
        const sessionId = searchParams.get('session_id');
        const mpToken = searchParams.get('marketplace_token');
        const mpStatus = searchParams.get('status');

        if (!paymentId && !sessionId && !mpToken) {
            navigate('/marketplace');
            return;
        }

        let verifyParam;
        if (sessionId) verifyParam = `session_id=${sessionId}`;
        else if (paymentId) verifyParam = `payment_id=${paymentId}`;
        else verifyParam = `marketplace_token=${mpToken}`;

        // If only marketplace_token (no payment_id), go straight to polling — payment may not be confirmed yet
        const startPending = !sessionId && !paymentId && mpToken;

        function goToSuccess(d) {
            setStatus('success');
            setTimeout(() => {
                navigate(`/marketplace/new?marketplace_token=${d.marketplace_token}&cust_id=${d.cust_id}&display_name=${encodeURIComponent(d.display_name)}`);
            }, 1500);
        }

        function startPolling(param) {
            setStatus('pending');
            let attempts = 0;
            const interval = setInterval(() => {
                attempts++;
                verifyMarketplacePayment(param).then(d => {
                    if (d.verified) {
                        clearInterval(interval);
                        goToSuccess(d);
                    }
                }).catch(() => {});
                if (attempts >= 30) clearInterval(interval);
            }, 4000);
        }

        if (startPending) {
            startPolling(verifyParam);
            return;
        }

        verifyMarketplacePayment(verifyParam)
            .then(data => {
                if (data.verified) {
                    if (!sessionId && mpStatus === 'pending') {
                        startPolling(verifyParam);
                    } else {
                        goToSuccess(data);
                    }
                } else {
                    setStatus('error');
                }
            })
            .catch(() => setStatus('error'));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="App">
            <Header />
            <main className="main-content">
                <div className="marketplace-new" style={{ textAlign: 'center', paddingTop: 60 }}>
                    {status === 'verifying' && (
                        <p style={{ color: '#9ba3ab' }}>{t('marketplace.paymentVerifying')}</p>
                    )}
                    {status === 'pending' && (
                        <>
                            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>⏳</div>
                            <h2 style={{ color: '#e8c84a' }}>{t('marketplace.paymentPending')}</h2>
                            <p style={{ color: '#9ba3ab', maxWidth: 420, margin: '0 auto' }}>
                                {t('marketplace.paymentFailedHint')}
                            </p>
                        </>
                    )}
                    {status === 'success' && (
                        <>
                            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>✅</div>
                            <h2 style={{ color: '#4caf50' }}>{t('marketplace.paymentSuccess')}</h2>
                            <p style={{ color: '#9ba3ab' }}>{t('marketplace.paymentRedirecting')}</p>
                        </>
                    )}
                    {status === 'error' && (
                        <>
                            <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>❌</div>
                            <h2 style={{ color: '#e07070' }}>{t('marketplace.paymentFailed')}</h2>
                            <p style={{ color: '#9ba3ab', maxWidth: 420, margin: '0 auto 20px' }}>
                                {t('marketplace.paymentFailedHint')}
                            </p>
                            <button
                                onClick={() => navigate('/marketplace')}
                                style={{ background: '#e8c84a', color: '#000', border: 'none', borderRadius: 6, padding: '10px 24px', fontWeight: 700, cursor: 'pointer' }}
                            >
                                {t('marketplace.backToList')}
                            </button>
                        </>
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}
